import { useMemo, useState } from 'react';
import { App, Button, Modal, Select } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import Editor from '@monaco-editor/react';
import {
  CODE_GEN_LANGUAGES,
  generateCode,
  type CodeGenLanguage,
  type CodeGenRequest,
} from '@presentation/services/codeGenerator';
import { useThemeStore } from '@presentation/stores/themeStore';

interface GenerateCodeModalProps {
  open: boolean;
  onClose: () => void;
  request: CodeGenRequest;
}

export const GenerateCodeModal = ({ open, onClose, request }: GenerateCodeModalProps) => {
  const { message } = App.useApp();
  const themeMode = useThemeStore((state) => state.mode);
  const [language, setLanguage] = useState<CodeGenLanguage>('curl');

  const code = useMemo(() => generateCode(request, language), [request, language]);
  const activeLanguage = CODE_GEN_LANGUAGES.find((option) => option.value === language);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      void message.success('Copied to clipboard');
    } catch {
      void message.error('Could not copy — your browser blocked clipboard access');
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={720}
      title="Generate code"
      destroyOnHidden
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-2">
          <Select
            size="small"
            value={language}
            onChange={setLanguage}
            options={CODE_GEN_LANGUAGES.map((option) => ({
              label: option.label,
              value: option.value,
            }))}
            className="w-64"
          />
          <Button size="small" icon={<CopyOutlined />} onClick={handleCopy}>
            Copy
          </Button>
        </div>
        <div className="h-96 overflow-hidden rounded border border-black/10 dark:border-white/10">
          <Editor
            height="100%"
            language={activeLanguage?.highlight ?? 'plaintext'}
            theme={themeMode === 'dark' ? 'vs-dark' : 'light'}
            value={code}
            options={{ readOnly: true, minimap: { enabled: false }, fontSize: 13, wordWrap: 'on' }}
          />
        </div>
      </div>
    </Modal>
  );
};
